import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import type { ReactNode } from "react";
import { Button } from "@shared/ui/button";
import { fail } from "@shared/lib/journal";

/**
 * Граница отказа: ловит поломку отрисовки и оставляет остальное приложение
 * живым.
 *
 * До неё любая ошибка в любом компоненте гасила окно целиком — человек видел
 * белый лист и не мог даже выйти. Причём хуже всего это вело себя ровно там,
 * где ошибка вероятнее: лента показывает чужой текст и чужие вложения, а
 * голосовой канал — чужие дорожки.
 *
 * Границ поэтому несколько, а не одна на приложение: поломка в ленте не должна
 * гасить список каналов, а поломка в голосе — переписку. Одна общая граница
 * ровно это и делала бы.
 *
 * Отказ уходит в журнал — в браузере он остаётся во вкладке, в собранном
 * приложении доезжает до файла. Это и есть ответ на «у меня всё пропало»:
 * видно, какой участок отвалился и на чём.
 */
export function Boundary({
  area,
  children,
  resetKeys,
}: {
  /** Что это за участок — попадёт в журнал и в подпись под сообщением. */
  area: string;
  children: ReactNode;
  /**
   * От чего граница отпускает сама.
   *
   * Обычно это то, что человек и так меняет руками: канал, сервер, вкладка.
   * Без этого один сорвавшийся канал оставался бы сорванным и после ухода в
   * соседний — граница не знает, что причина сменилась.
   */
  resetKeys?: unknown[];
}) {
  return (
    <ErrorBoundary
      FallbackComponent={Broken}
      resetKeys={[area, ...(resetKeys ?? [])]}
      onError={(error, info) => {
        fail("экран", `${area}: отрисовка сорвалась`, error, {
          где: info.componentStack?.trim().split("\n")[0]?.trim(),
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Что видно вместо сорвавшегося участка.
 *
 * Текст ошибки показывается: это не утечка — сообщения здешние, а человеку,
 * который пришёл с этим в переписку, только оно и поможет. Кнопка пробует
 * собрать участок заново: половина поломок отрисовки одноразовая — пришло
 * событие с полем, которого не ждали.
 */
function Broken({ error, resetErrorBoundary }: FallbackProps) {
  const because = error instanceof Error ? error.message : String(error);

  return (
    <div className="empty" role="alert">
      <div className="empty-mark" />
      <div className="empty-title">Здесь сломалось</div>
      <div className="empty-body">
        Остальное приложение работает — можно уйти в соседний канал. Подробности
        записаны в журнал: настройки, вкладка «Журнал».
      </div>
      <Button
        type="button"
        size="sm"
        style={{ alignSelf: "flex-start" }}
        onClick={resetErrorBoundary}
      >
        Попробовать снова
      </Button>
      <div className="empty-note">{because}</div>
    </div>
  );
}
